/**
 * PolicySlice Selectors
 * 
 * Memoized selectors for accessing policy slice state.
 * Uses createSelector for performance optimization.
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { PolicySliceState } from './state';

// Base selector to get the policy slice
const selectPolicySlice = (state: RootState) => state.policy as unknown as PolicySliceState;

// =============================================================================
// CORE SELECTORS
// =============================================================================

/**
 * Select the current policy entity
 */
export const selectCurrentPolicy = createSelector(
  selectPolicySlice,
  (slice) => slice.currentPolicy
);

/**
 * Select whether current policy exists
 */
export const selectHasCurrentPolicy = createSelector(
  selectCurrentPolicy,
  (policy) => policy !== null
);

/**
 * Select the policy ID (if exists)
 */
export const selectPolicyId = createSelector(
  selectCurrentPolicy,
  (policy) => policy?.id
);

/**
 * Select all policies
 */
export const selectPolicies = createSelector(
  selectPolicySlice,
  (slice) => slice.policies
);

/**
 * Select the selected company
 */
export const selectSelectedCompany = createSelector(
  selectPolicySlice,
  (slice) => slice.selectedCompany
);

// =============================================================================
// EDIT TRACKING SELECTORS
// =============================================================================

/**
 * Select whether any edits have been made
 */
export const selectPolicyIsEdited = createSelector(
  selectPolicySlice,
  (slice) => slice.isEdited
);

/**
 * Select the list of edited field names
 */
export const selectPolicyEditedFields = createSelector(
  selectPolicySlice,
  (slice) => slice.editedFields
);

/**
 * Factory to create a selector for checking if a specific field is edited
 */
export const selectIsPolicyFieldEdited = (fieldName: string) =>
  createSelector(selectPolicyEditedFields, (fields) => fields.includes(fieldName));

// =============================================================================
// VALIDATION SELECTORS
// =============================================================================

/**
 * Select all validation errors
 */
export const selectPolicyValidationErrors = createSelector(
  selectPolicySlice,
  (slice) => slice.validationErrors
);

/**
 * Select whether there are any validation errors
 */
export const selectHasPolicyValidationErrors = createSelector(
  selectPolicyValidationErrors,
  (errors) => Object.keys(errors).length > 0
);

/**
 * Factory to create a selector for a specific field's validation error
 */
export const selectPolicyValidationError = (fieldName: string) =>
  createSelector(selectPolicyValidationErrors, (errors) => errors[fieldName]);

// =============================================================================
// SUB-ENTITY SELECTORS
// =============================================================================

/**
 * Select effective procedure codes
 */
export const selectEffectiveProcedureCodes = createSelector(
  selectCurrentPolicy,
  (policy) => policy?.effectiveProcedureCodes ?? []
);

/**
 * Select effective insurances
 */
export const selectEffectiveInsurances = createSelector(
  selectCurrentPolicy,
  (policy) => policy?.effectiveInsurances ?? []
);

/**
 * Select policy summary
 */
export const selectPolicySummary = createSelector(
  selectCurrentPolicy,
  (policy) => policy?.policySummary
);
